import type { LucideIcon } from "lucide-react";
import {
  Users,
  BarChart,
  Home,
  MessageSquare,
  UserSquare2,
  FileText,
  BookOpen,
  TrendingUp,
  HandCoins,
  CreditCard,
  ReceiptText,
  ArrowLeftRight,
  BookOpenCheck,
  Calculator,
  FileSpreadsheet,
  Receipt,
  Scale,
  WalletCards,
  CircleHelp,
  Ticket,
  Layers,
  ShieldCheck,
  ListChecks,
  LayoutTemplate,
  Megaphone,
} from "lucide-react";

import type { AccessContext, TaDeskCapability } from "@sdk/access";
import { hasAnyCapability } from "@sdk/access/resolve-access-context";

export interface NavItemConfig {
  title: string;
  url: string;
  icon?: LucideIcon;
  disabled?: boolean;
  hideWhenDenied?: boolean;
  insiderOnly?: boolean;
  anyCapability?: TaDeskCapability[];
  salesAccess?: boolean;
  subItems?: NavItemConfig[];
  /** Renders a divider above this item within the parent group. */
  separatorBefore?: boolean;
}

export interface NavGroupConfig {
  title: string;
  icon?: LucideIcon;
  hideLabel?: boolean;
  collapsible?: boolean;
  disabled?: boolean;
  hideWhenDenied?: boolean;
  insiderOnly?: boolean;
  anyCapability?: TaDeskCapability[];
  items: NavItemConfig[];
}

export const NAV_GROUPS: NavGroupConfig[] = [
  {
    title: "Home",
    hideLabel: true,
    collapsible: false,
    items: [
      { title: "Home", url: "/dashboard", icon: Home },
      {
        title: "My Customers",
        url: "/my/customers",
        icon: UserSquare2,
        salesAccess: true,
      },
      {
        title: "My Enquiries",
        url: "/my/enquiries",
        icon: MessageSquare,
        anyCapability: ["MANAGE_ENQUIRIES"],
      },
      {
        title: "My Quotes",
        url: "/my/quotes",
        icon: FileText,
        anyCapability: ["MANAGE_QUOTES"],
      },
      {
        title: "My Bookings",
        url: "/my/bookings",
        icon: BookOpen,
        anyCapability: ["MANAGE_BOOKINGS"],
        insiderOnly: true,
      },
      {
        title: "My Performance",
        url: "/my/performance",
        icon: TrendingUp,
        anyCapability: ["MANAGE_ENQUIRIES", "MANAGE_QUOTES", "MANAGE_BOOKINGS"],
      },
      {
        title: "Guides",
        url: "/guides",
        icon: CircleHelp,
        separatorBefore: true,
      },
    ],
  },
  {
    title: "CRM",
    icon: Users,
    collapsible: false,
    items: [
      { title: "All Customers", url: "/customers", icon: UserSquare2 },
      {
        title: "All Enquiries",
        url: "/enquiries",
        icon: MessageSquare,
        anyCapability: ["VIEW_TEAM_ENQUIRIES"],
      },
      {
        title: "All Quotes",
        url: "/quotes",
        icon: FileText,
        anyCapability: ["VIEW_TEAM_QUOTES"],
      },
      {
        title: "Quote Templates",
        url: "/quote-templates",
        icon: LayoutTemplate,
        anyCapability: ["MANAGE_QUOTE_TEMPLATES"],
        hideWhenDenied: true,
      },
      {
        title: "Campaign Creator",
        url: "/campaign-creator",
        icon: Megaphone,
        anyCapability: ["ADMIN_ACCOUNT"],
        insiderOnly: true,
      },
      {
        title: "All Bookings",
        url: "/bookings",
        icon: BookOpen,
        anyCapability: ["VIEW_ALL_BOOKINGS", "VIEW_TEAM_BOOKINGS"],
        insiderOnly: true,
      },
      {
        title: "Credit Vouchers",
        url: "/customer-credit-vouchers",
        icon: Ticket,
        insiderOnly: true,
      },
    ],
  },
  {
    title: "Reports",
    icon: BarChart,
    collapsible: false,
    insiderOnly: true,
    anyCapability: ["VIEW_REPORTS"],
    items: [
      {
        title: "Performance Report",
        url: "/reports/performance",
        icon: TrendingUp,
        anyCapability: ["VIEW_REPORTS"],
      },
      {
        title: "ATOL Licensable Bookings",
        url: "/reports/atol/licensable-bookings",
        icon: ShieldCheck,
        anyCapability: ["VIEW_REPORTS"],
      },
      {
        title: "ATOL ARA Export Pack",
        url: "/reports/atol/ara-pack",
        icon: ShieldCheck,
        anyCapability: ["VIEW_REPORTS"],
      },
      {
        title: "ATOL APC Returns",
        url: "/reports/atol/apc",
        icon: ShieldCheck,
        anyCapability: ["VIEW_REPORTS"],
      },
    ],
  },
  {
    title: "Accounting",
    icon: Calculator,
    collapsible: false,
    insiderOnly: true,
    anyCapability: ["VIEW_ACCOUNTING"],
    items: [
      {
        title: "Customer Invoices",
        url: "/accounting/invoices",
        icon: FileSpreadsheet,
        anyCapability: ["VIEW_ACCOUNTING"],
      },
      {
        title: "Customer Invoice Payments",
        url: "/accounting/customer-invoice-payments",
        icon: CreditCard,
        anyCapability: ["VIEW_ACCOUNTING"],
      },
      {
        title: "Credit Vouchers",
        url: "/customer-credit-vouchers",
        icon: Ticket,
        anyCapability: ["VIEW_ACCOUNTING"],
        insiderOnly: true,
      },
      {
        title: "Supplier Bills",
        url: "/accounting/bills",
        icon: Receipt,
        anyCapability: ["VIEW_ACCOUNTING"],
      },
      {
        title: "Supplier Bill Payments",
        url: "/accounting/supplier-bill-payments",
        icon: HandCoins,
        anyCapability: ["VIEW_ACCOUNTING"],
      },
      {
        title: "Profit Allocation Records",
        url: "/accounting/profit-allocation",
        icon: ReceiptText,
        anyCapability: ["VIEW_ACCOUNTING"],
      },
      {
        title: "Manual Journals",
        url: "/accounting/manual-journals",
        icon: BookOpenCheck,
        anyCapability: ["VIEW_ACCOUNTING"],
        separatorBefore: true,
      },
      {
        title: "Trial Balance",
        url: "/accounting/trial-balance",
        icon: Scale,
        anyCapability: ["VIEW_ACCOUNTING"],
      },
      {
        title: "Ledgers",
        url: "/ledger-transactions",
        icon: ArrowLeftRight,
        anyCapability: ["VIEW_ACCOUNTING"],
      },
      {
        title: "How accounting works",
        url: "/accounting/guide",
        icon: CircleHelp,
        anyCapability: ["VIEW_ACCOUNTING"],
      },
      {
        title: "Reconciliation",
        url: "/accounting/reconciliation",
        icon: ListChecks,
        anyCapability: ["VIEW_ACCOUNTING"],
        separatorBefore: true,
      },
      {
        title: "Bulk Pay Supplier Bills",
        url: "/accounting/bulk-pay-supplier-bills",
        icon: Layers,
        anyCapability: ["VIEW_ACCOUNTING"],
      },
      {
        title: "Import Customer Receipts",
        url: "/accounting/import-customer-receipts",
        icon: FileSpreadsheet,
        anyCapability: ["VIEW_ACCOUNTING"],
      },
      {
        title: "Trade Expenses",
        url: "/finance/trade-expenses",
        icon: WalletCards,
        anyCapability: ["VIEW_ACCOUNTING"],
      },
    ],
  },
];

export function isNavItemVisible(
  ctx: AccessContext | null,
  item: Pick<NavItemConfig, "anyCapability" | "salesAccess">
): boolean {
  if (!ctx) return false;
  if (ctx.isOwner) return true;

  if (item.salesAccess) {
    return hasAnyCapability(ctx, [
      "MANAGE_CONTACTS",
      "MANAGE_ENQUIRIES",
      "MANAGE_QUOTES",
      "MANAGE_BOOKINGS",
    ]);
  }

  if (!item.anyCapability?.length) {
    return true;
  }

  return hasAnyCapability(ctx, item.anyCapability);
}

export function isNavItemTreeVisible(
  ctx: AccessContext | null,
  item: Pick<NavItemConfig, "anyCapability" | "salesAccess" | "subItems">
): boolean {
  if (item.subItems?.length) {
    return item.subItems.some((subItem) => isNavItemTreeVisible(ctx, subItem));
  }
  return isNavItemVisible(ctx, item);
}

function filterNavItems(
  ctx: AccessContext | null,
  items: NavItemConfig[]
): NavItemConfig[] {
  return items
    .map((item) => {
      if (item.subItems?.length) {
        const visibleSubItems = filterNavItems(ctx, item.subItems);
        if (!visibleSubItems.length) return null;
        return { ...item, subItems: visibleSubItems };
      }
      return isNavItemVisible(ctx, item) ? item : null;
    })
    .filter(Boolean) as NavItemConfig[];
}

function applyInsiderGatingToItem(
  item: NavItemConfig,
  groupDisabled: boolean,
  isInsider: boolean
): NavItemConfig {
  const itemDisabled =
    groupDisabled ||
    Boolean(item.insiderOnly && !isInsider) ||
    item.disabled;

  return {
    ...item,
    disabled: itemDisabled,
    subItems: item.subItems?.map((subItem) =>
      applyInsiderGatingToItem(subItem, groupDisabled, isInsider)
    ),
  };
}

export function isNavGroupVisible(
  ctx: AccessContext | null,
  group: Pick<NavGroupConfig, "anyCapability" | "items">
): boolean {
  if (!ctx) return false;
  if (ctx.isOwner) return true;

  if (group.anyCapability?.length) {
    return hasAnyCapability(ctx, group.anyCapability);
  }

  return group.items.some((item) => isNavItemTreeVisible(ctx, item));
}

function applyInsiderGating(
  group: NavGroupConfig,
  isInsider: boolean
): NavGroupConfig {
  const groupDisabled = Boolean(group.insiderOnly && !isInsider);

  return {
    ...group,
    disabled: groupDisabled || group.disabled,
    items: group.items.map((item) =>
      applyInsiderGatingToItem(item, groupDisabled, isInsider)
    ),
  };
}

export function filterNavGroups(
  ctx: AccessContext | null,
  groups: NavGroupConfig[] = NAV_GROUPS,
  isInsider = false
) {
  return groups
    .map((group) => {
      const visibleItems = filterNavItems(ctx, group.items);
      if (!visibleItems.length && group.hideWhenDenied !== false) {
        return null;
      }
      if (!isNavGroupVisible(ctx, group) && group.hideWhenDenied !== false) {
        return null;
      }
      return applyInsiderGating(
        {
          ...group,
          items: visibleItems,
        },
        isInsider
      );
    })
    .filter(Boolean) as NavGroupConfig[];
}
